/**
 * SEO 组件 - 统一处理 title / description / canonical / hreflang / og: / Twitter Card
 *
 * 用法：
 *   <SEO
 *     pageKey="home"
 *     title={{ zh: '...', en: '...', vi: '...' }}
 *     description={{ zh: '...', en: '...', vi: '...' }}
 *     keywords="铺布机, 小麦, ..."
 *     slug=""     // 路由 slug（首页传 ''，关于传 'about'）
 *   />
 *
 * 路径从 useLocation() 自动获取，配合 siteConfig.LANG_PREFIX 生成 canonical
 */

import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DOMAIN, LANG_FROM_PATH, LANGS } from '../../config/siteConfig';

const LANG_OG_LOCALE = {
  zh: 'zh_CN',
  en: 'en_US',
  vi: 'vi_VN',
};

const SEO = ({
  pageKey,           // 用于 key 唯一性，无业务作用
  title,             // { zh, en, vi }
  description,       // { zh, en, vi }
  keywords,          // 字符串，逗号分隔（所有语言共用）
  slug,              // 路由 slug，如 'about' / 'x5'，首页传 ''
  image = 'https://oss.xiaomaizn.com/xiaomai/xiaomai%E5%AE%98%E7%BD%91/files/images/big1.webp',
  type = 'website',
  noindex = false,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();

  const currentLang = useMemo(() => {
    // i18n 实际加载的语言（en/zh/vi）；路径驱动优先
    const pathLang = LANG_FROM_PATH(location.pathname);
    return LANGS.includes(pathLang) ? pathLang : (LANGS.includes(i18n.language) ? i18n.language : 'en');
  }, [location.pathname, i18n.language]);

  // 当前页面的 canonical URL
  const currentUrl = useMemo(() => {
    const prefix = currentLang === 'en' ? '' : `/${currentLang}`;
    const path = slug ? `${prefix}/${slug}` : `${prefix}/`;
    return `${DOMAIN}${path}`;
  }, [currentLang, slug]);

  // 三语 alternate 链接（hreflang）
  const alternateLinks = useMemo(() => {
    return LANGS.map((lang) => {
      const prefix = lang === 'en' ? '' : `/${lang}`;
      const path = slug ? `${prefix}/${slug}` : `${prefix}/`;
      return { lang, href: `${DOMAIN}${path}` };
    });
  }, [slug]);

  const langTitle = title?.[currentLang] || title?.en || 'Xiaomai Technology';
  const langDesc = description?.[currentLang] || description?.en || '';

  return (
    <Helmet key={pageKey || currentUrl} htmlAttributes={{ lang: currentLang }}>
      <title>{langTitle}</title>
      {langDesc && <meta name="description" content={langDesc} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large" />
      }

      {/* Canonical */}
      <link rel="canonical" href={currentUrl} />

      {/* hreflang 三语 alternate */}
      {alternateLinks.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang} href={a.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${DOMAIN}${slug ? '/' + slug : '/'}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="小麦铺布机 - 龙南小麦自动化科技有限公司" />
      <meta property="og:title" content={langTitle} />
      {langDesc && <meta property="og:description" content={langDesc} />}
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={LANG_OG_LOCALE[currentLang]} />
      {LANGS.filter((l) => l !== currentLang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={LANG_OG_LOCALE[l]} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={langTitle} />
      {langDesc && <meta name="twitter:description" content={langDesc} />}
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SEO;
